import React, { useState, useEffect, useCallback } from 'react';

import { parseISO, format } from 'date-fns';
import { Button, Container } from 'react-bootstrap';
import {
  FaPrint,
  FaTrashAlt,
  FaExclamationCircle,
  FaFileDownload,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import { ORIGEM_CONTRATO } from '../../../utils/funcoes';

import Loading from '../../Loading';
import getResponseErrors from '../../../utils/getResponseErrors';
import Assinado from './Assinado';
import Modal from '../../Modal';
import ModalAssinaturaContrato from './ModalAssinaturaContrato';

interface IContrato {
  ConCodigo: number;
  ConDataInicio: Date;
  ConDataFim: Date;
  ConDataInicioFormatado: string;
  ConDataFimFormatado: string;
  ConValor: number;
  ConTipo: 1 | 2 | 3 | 4 | 6 | 7 | 10 | 11;
  ConTitulo: string;
  ConAssinado: boolean;
  ConArquivo?: string;
  ConExternalId?: string;
  ConMetadados: Record<string, unknown>;
  arquivo_url?: string;
  podeRefazer: boolean;
}

export interface IContratoAssinado {
  ConCodigo: number;
  ConAssinado: boolean;
  PlaContratoAssinado: boolean;
}

export interface IPacientePlano {
  PacCodigo: number;
  PacNome: string;
  PacNomeSocial: string;
  PacCPF: string;
  PacCNPJ: string;
  PacEmail: string;
  PacPessoaFisica: boolean;
}

interface IReturnDependentes {
  DepResponsavel: boolean;
  DepTitular: boolean;
  DepDataExclusao?: Date;
  paciente: IPacientePlano;
}

type ComponentProps = {
  PlaCodigo: number;
  handleAssinouContrato(contratoAssinado: boolean): void;
  handleFecharModal(): void;
};

const CartaoSaudeContrato: React.FC<ComponentProps> = ({
  PlaCodigo,
  handleAssinouContrato,
  handleFecharModal,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [assinaDigital, setAssinaDigital] = useState(false);
  const [contratoRefazer, setContratoRefazer] = useState<number>();

  const [contratos, setContratos] = useState<IContrato[] | undefined>(
    undefined,
  );

  const [
    pacienteResponsavel,
    setPacienteResponsavel,
  ] = useState<IPacientePlano | null>(null);
  const [
    contratoParaAssinar,
    setContratoParaAssinar,
  ] = useState<IContrato | null>(null);
  const [modalAssinaturaAberto, setModalAssinaturaAberto] = useState(false);

  const handleLoadDados = useCallback(
    async (plano_id: number) => {
      setLoading(true);
      try {
        const [
          responseContratos,
          responseDependentes,
          responseDigital,
        ] = await Promise.all([
          api.get<IContrato[]>(`planos/contratoscartao/${plano_id}`),
          api.get<IReturnDependentes[]>(
            `planos/dependentes/responsavel/${plano_id}`,
          ),
          api.get<{ digital: boolean }>(`planos/contrato/assinadigital`),
        ]);

        setAssinaDigital(responseDigital.data.digital);

        const contratosFormatados = responseContratos.data.map(con => {
          return {
            ...con,
            ConDataInicioFormatado: format(
              parseISO(con.ConDataInicio.toString()),
              'dd/MM/yyyy',
            ),
            ConDataFimFormatado: format(
              parseISO(con.ConDataFim.toString()),
              'dd/MM/yyyy',
            ),
            ConTitulo: ORIGEM_CONTRATO[con.ConTipo],
            podeRefazer:
              !con.ConAssinado || (con.ConAssinado && !con.ConExternalId),
          };
        });

        setContratos(contratosFormatados);

        const dependentes = responseDependentes.data;
        if (dependentes) {
          const responsavel =
            dependentes.find(d => d.DepResponsavel && !d.DepDataExclusao) ||
            dependentes.find(d => d.DepTitular && !d.DepDataExclusao) ||
            dependentes[0];

          if (responsavel?.paciente) {
            const { paciente } = responsavel;
            setPacienteResponsavel({
              PacCodigo: paciente.PacCodigo,
              PacNome: paciente.PacNome,
              PacNomeSocial: paciente.PacNomeSocial,
              PacCPF: paciente.PacCPF,
              PacCNPJ: paciente.PacCNPJ,
              PacEmail: paciente.PacEmail,
              PacPessoaFisica: paciente.PacPessoaFisica,
            });
          }
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (PlaCodigo && PlaCodigo > 0) handleLoadDados(PlaCodigo);
  }, [PlaCodigo, addToast, handleLoadDados]);

  const handleSetAssinado = useCallback(
    ({ PlaContratoAssinado, ConCodigo, ConAssinado }: IContratoAssinado) => {
      const novoArray = contratos?.map(itens => {
        if (itens.ConCodigo === ConCodigo) return { ...itens, ConAssinado };
        return itens;
      });

      setContratos(novoArray);
      handleAssinouContrato(PlaContratoAssinado);
    },
    [contratos, handleAssinouContrato],
  );

  const handleRefazContrato = useCallback(
    async (conCodigo: number) => {
      setLoading(true);
      try {
        await api.put(`/planos/contrato/${conCodigo}`);
        handleLoadDados(PlaCodigo);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível refazer o contrato',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
        setContratoRefazer(undefined);
      }
    },
    [PlaCodigo, addToast, handleLoadDados],
  );

  const handleAbrirModalAssinatura = useCallback((contrato: IContrato) => {
    setContratoParaAssinar(contrato);
    setModalAssinaturaAberto(true);
  }, []);

  const handleFecharModalAssinatura = useCallback(() => {
    setContratoParaAssinar(null);
    setModalAssinaturaAberto(false);
  }, []);

  const handleAssinaturaIniciada = useCallback(() => {
    handleLoadDados(PlaCodigo);
    handleFecharModalAssinatura();
    handleFecharModal();
  }, [
    handleLoadDados,
    PlaCodigo,
    handleFecharModalAssinatura,
    handleFecharModal,
  ]);

  // const handleAbreParaAssinatura = useCallback(
  //   (ConCodigo: number, plano_id: number) => {
  //     handleFecharModal();
  //     window.open(`/cartaassinatura/${ConCodigo}/${plano_id}`, '_blank');
  //   },
  //   [handleFecharModal],
  // );

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}

      {contratoRefazer && (
        <Modal
          handleFecharModal={() => setContratoRefazer(undefined)}
          show={!!contratoRefazer}
          titulo="Refazer Contrato"
          large={false}
        >
          <div className="text-center">
            <FaExclamationCircle size={48} color="#dc3545" />
            <br />
            <br />
            <h6>
              Atenção, essa operação irá EXCLUIR o arquivo assinado, caso
              exista.
              <br />
              <br />
              ESTE PROCESSO É IRREVERSÍVEL, deseja continuar?
            </h6>
          </div>
          <div className="d-flex justify-content-between mt-2">
            <Button
              type="button"
              variant="danger"
              onClick={() => handleRefazContrato(contratoRefazer)}
              size="sm"
            >
              <FaTrashAlt /> Sim, excluir
            </Button>

            <Button
              type="submit"
              variant="secondary"
              size="sm"
              onClick={() => setContratoRefazer(undefined)}
            >
              Não, voltar
            </Button>
          </div>
        </Modal>
      )}

      {contratos && contratos.length > 0 && (
        <table
          className="table table-hover table-sm table-font-small table-bordered"
          style={{ borderWidth: '0px' }}
        >
          <thead>
            <tr className="bg-light text-center">
              <th style={{ minWidth: '100px' }}>Contrato</th>
              <th style={{ minWidth: '100px' }}>Data Início</th>
              <th style={{ minWidth: '100px' }}>Data Fim</th>
              <th style={{ width: '70px' }}>Visualizar</th>
              {/* <th style={{ width: '70px' }}>Assinado</th> */}
              <th style={{ width: '70px' }}>
                {assinaDigital ? 'Assinatura' : 'Assinado'}
              </th>
              <th style={{ width: '70px' }}>-</th>
            </tr>
          </thead>
          <tbody>
            {contratos &&
              contratos.map(rel => (
                <tr
                  key={`des-${rel.ConCodigo}`}
                  style={{
                    fontSize: 'Small',
                    height: '30px',
                  }}
                >
                  <td>{rel.ConTitulo}</td>
                  <td>{rel.ConDataInicioFormatado}</td>
                  <td>{rel.ConDataFimFormatado}</td>
                  <td>
                    {rel.arquivo_url && (
                      <a
                        style={{ textAlign: 'center' }}
                        href={rel.arquivo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaFileDownload size={26} />
                      </a>
                    )}
                    {!rel.arquivo_url && (
                      <Link
                        style={{ textAlign: 'center' }}
                        to={`/cartaosaudeimpressao/${rel.ConCodigo}/contrato`}
                        target="_blank"
                      >
                        <FaPrint size={26} />
                      </Link>
                    )}
                  </td>
                  <td className="text-center">
                    {rel.ConAssinado &&
                      rel.ConExternalId &&
                      'Assinatura Digital'}
                    {rel.ConAssinado &&
                      !rel.ConExternalId &&
                      'Assinatura Manual'}
                    {!assinaDigital && (
                      <Assinado
                        ConCodigo={rel.ConCodigo}
                        ConAssinado={rel.ConAssinado}
                        ConDigital={!!rel.arquivo_url}
                        handleAssinado={handleSetAssinado}
                      />
                    )}
                    {assinaDigital && !rel.ConAssinado && (
                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleAbrirModalAssinatura(rel)}
                        disabled={loading}
                      >
                        Assinar
                      </Button>
                    )}
                  </td>
                  <td>
                    {rel.podeRefazer && (
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        className="float-right ml-2"
                        onClick={() => setContratoRefazer(rel.ConCodigo)}
                        disabled={loading}
                      >
                        Refazer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}

      {modalAssinaturaAberto && (
        <ModalAssinaturaContrato
          contrato={contratoParaAssinar}
          paciente={pacienteResponsavel}
          show={modalAssinaturaAberto}
          onClose={handleFecharModalAssinatura}
          onAssinaturaIniciada={handleAssinaturaIniciada}
        />
      )}
    </Container>
  );
};

export default CartaoSaudeContrato;
